/**
 * Whether this browser believes it has a session. **Not the session itself.**
 *
 * ── What this used to be ─────────────────────────────────────────────────────
 *
 * The signed-in email, stored in `localStorage`, sent as `?email=` on every
 * request, and trusted by the API. It was a credential in the sense that
 * changing it changed who you were — and it was readable and writable by any
 * script on the page and by anybody who opened devtools. The file said so at
 * the time; it is still the right thing to say, and it is why none of it is
 * here any more.
 *
 * ── What it is now ───────────────────────────────────────────────────────────
 *
 * A flag. The real session is an opaque token in an `HttpOnly; SameSite=Strict`
 * cookie that this code **cannot read**, cannot forge and cannot move to another
 * site. The server resolves it against `user_sessions` on every request.
 *
 * The flag exists for exactly one reason: to avoid flashing the sign-in card at
 * somebody who is already signed in. `App` reads it synchronously as its initial
 * state, renders the shell, and the first `GET /api/session` either confirms it
 * or answers 401 — at which point `onSessionRejected` clears the flag and the
 * sign-in card appears once, rather than on every refresh.
 *
 * So the worst an attacker achieves by setting `biovet.signed-in` to `1` is a
 * loading spinner followed by a sign-in screen. That is the whole of its
 * authority, and it is the point of the change.
 *
 * `localStorage` rather than `sessionStorage`, still: an accountant working
 * through a day's vouchers opens the ledger in a second tab, and a per-tab flag
 * would make that look like a second sign-in.
 */

const HINT_KEY = 'biovet.signed-in';

/** Guarded because Safari private mode throws on `localStorage` access. */
function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function hasSignInHint(): boolean {
  return storage()?.getItem(HINT_KEY) === '1';
}

export function rememberSignIn(): void {
  try {
    storage()?.setItem(HINT_KEY, '1');
  } catch {
    // A full quota or a blocked store costs one extra sign-in card on refresh,
    // not the sign-in.
  }
}

export function forgetSignIn(): void {
  try {
    storage()?.removeItem(HINT_KEY);
    // The old key held an email and is a credential-shaped thing to leave lying
    // around in a browser that has been upgraded. Removed on the way past.
    storage()?.removeItem('biovet.session.email');
  } catch {
    // Nothing to do: the in-memory state is cleared by the caller regardless.
  }
}
